import React, { useMemo, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/router'
import { useQuery, useMutation } from '@apollo/client'
import QuestionFactory from '../question-types/question-factory'
import ProgressIndicator from '../progress-indicator'
import NavigationControls from '../navigation-controls'
import SimpleKickoffHeader from 'components/payment/common/simple-kickoff-header'
import { createSurveyQuestionResponses as createSurveyQuestionResponsesMutation } from 'hooks/use-survey/mutations.graphql'
import { getClientResponses } from 'components/shared/cms-survey/queries.graphql'
import {
  updateTimeZone,
  updateLead,
} from 'components/client-sign-up/mutations.graphql'
import { useSurveyStore } from 'stores/survey-store'
import { validateAnswer } from '../validation'
import { prepareSubmissionPayload } from '../submission'
import { filterQuestions } from '../question-filter'
import { filterResponseOptions } from '../response-filter'
import s from './styles.module.css'
import useTargetAudience, { Audience } from 'hooks/use-target-audience'
import { useMediaQuery } from 'hooks/use-media-query'
import useScrollToTop from 'hooks/use-scroll-to-top'
import AnalyticsManager from 'utilities/analytics-manager'
import { usePrepopulateState } from 'hooks/use-prepopulate-state'

interface SurveyQuestion {
  id: number
  text: string
  type: string
  isRequired?: boolean
  maxSelections?: number
  modelPath?: string
  component?: string
  responses?: Array<{
    id: number
    text: string
    nestedQuestions?: SurveyQuestion[]
  }>
  hideText?: boolean
}

interface SurveySection {
  id: number
  name: string
  heading?: string
  questions: SurveyQuestion[]
}

interface Survey {
  id: number
  name: string
  sections: SurveySection[]
}

interface SurveyQuestionResponse {
  id?: number
  surveyQuestionId: number
  surveyQuestionResponseId?: number
  responseValue?: string | null
  metadata?: {
    skipMembershipId?: boolean
    [key: string]: any
  }
  hideText?: boolean
}

interface QuestionPageProps {
  survey: Survey
  entryId: string
  currentQuestionId: number | string
  clientId?: number
  user?: any
  coachId?: number
  leadId?: number
  existingResponses?: SurveyQuestionResponse[]
  leadInfo?: {
    landingPageUrl?: string
    deviceType?: string
    utmCampaignId?: string
    utmCampaign?: string
    utmSource?: string
    utmMedium?: string
    utmTerm?: string
    utmContent?: string
    referrer?: string
    userAgent?: string
  }
  isImpersonation?: boolean
}

const QuestionPage: React.FC<QuestionPageProps> = ({
  survey,
  currentQuestionId: initialQuestionId,
  user,
  clientId,
  coachId,
  leadId,
  existingResponses = [],
  leadInfo,
  isImpersonation,
}) => {
  const router = useRouter()
  const targetAudience = useTargetAudience()
  const isMobile = useMediaQuery('(max-width: 768px)')
  // TODO: add back in when we have a free trial target audience
  // const isFreeTrial = targetAudience === Audience.FreeTrial
  const isGuestPass = targetAudience === Audience.GuestPass

  const [updateTimeZoneMutation] = useMutation(updateTimeZone)
  const [updateLeadMutation] = useMutation(updateLead)

  // Ref for the scroll container to scroll to top on question change
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Sync timezone + DST-aware utcOffset from the browser.
  // Why: getServerSideProps can't read the visitor's tz, and a stored offset
  // goes stale across DST transitions. Browser tz is authoritative; recompute
  // offset for "now" so winter/summer values stay correct.
  // Skip during impersonation — the browser belongs to staff, not the client.
  // Round the offset because users.utc_offset is an integer column; clients
  // in fractional zones (IST 5.5, NPT 5.75) would otherwise fail the Int
  // GraphQL validation and never get either field saved.
  useEffect(() => {
    if (!user?.id || !clientId || isImpersonation) return
    const browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    const browserUtcOffset = Math.round(-new Date().getTimezoneOffset() / 60)
    if (
      user.timeZone === browserTimeZone &&
      user.utcOffset === browserUtcOffset
    ) {
      return
    }
    updateTimeZoneMutation({
      variables: {
        id: user.id,
        timeZone: browserTimeZone,
        utcOffset: browserUtcOffset,
      },
    }).catch(error => {
      console.error('Failed to update timezone:', error)
    })
  }, [user, clientId, isImpersonation, updateTimeZoneMutation])

  // Update lead with tracking information
  useEffect(() => {
    if (leadId && leadInfo) {
      updateLeadMutation({
        variables: {
          id: String(leadId),
          ...leadInfo,
        },
      }).catch(error => {
        console.error('Failed to update lead:', error)
      })
    }
  }, [leadId, leadInfo, updateLeadMutation])

  // Get current questionId from router query (for shallow routing) or fallback to prop
  const currentQuestionId =
    (router.query.questionId as string) || initialQuestionId

  // Scroll to top of scroll container when question changes
  // Pass currentQuestionId as dependency since we use shallow routing
  useScrollToTop(scrollContainerRef, currentQuestionId, {
    alsoScrollWindow: true,
  })

  // Flatten all questions from all sections
  const allQuestions = useMemo(
    () => survey.sections.flatMap(section => section.questions),
    [survey]
  )

  // Zustand store - get all needed state and actions
  const {
    setAnswer,
    setSkipValue,
    setMetadata,
    setError: setStoreError,
    setTouched,
    getAnswer,
    getSkipValue,
    setSubmitting,
    isSubmitting,
    initializeFromResponses,
    isInitialized,
    answers: allAnswers,
    setLatestEligibilityParamsFromDB,
    clearAnswer,
  } = useSurveyStore()

  // Initialize latest eligibility params from database
  useEffect(() => {
    const eligibilityParams =
      user?.client?.latestClientEligibilityStatus?.eligibilityParams || null
    setLatestEligibilityParamsFromDB(eligibilityParams)
  }, [
    user?.client?.latestClientEligibilityStatus,
    setLatestEligibilityParamsFromDB,
  ])

  // Get all answers from Zustand store for conditional filtering
  // Filter questions based on conditional logic
  const filteredQuestions = useMemo(() => {
    return filterQuestions(allQuestions, allAnswers, isGuestPass)
  }, [allQuestions, allAnswers, isGuestPass])

  // Find current question from filtered questions and filter its response options
  const currentQuestion = useMemo((): SurveyQuestion | undefined => {
    const question =
      filteredQuestions.find(q => String(q.id) === String(currentQuestionId)) ||
      filteredQuestions[0]

    // Filter response options based on other question answers
    return question ? filterResponseOptions(question, allAnswers) : undefined
  }, [filteredQuestions, currentQuestionId, allAnswers])

  // Clear question 400's answer if question 219 has no responses
  useEffect(() => {
    const question219Id = '219'
    const question400Id = '400'
    const question219Responses = allAnswers[question219Id]

    // Check if question 219 has no responses or no valid response IDs
    const hasNoValidResponses =
      !question219Responses ||
      question219Responses.length === 0 ||
      !question219Responses.some(
        response => response.surveyQuestionResponseId != null
      )

    // If question 219 has no valid responses and question 400 has an answer, clear it
    if (hasNoValidResponses && allAnswers[question400Id]) {
      clearAnswer(question400Id)
    }
  }, [allAnswers, clearAnswer])

  // Pre-populate state question query param (mobile app)
  usePrepopulateState({
    currentQuestionId,
    hasAnswer: !!getAnswer(String(currentQuestionId)),
    setAnswer,
    routerQuery: router.query,
  })

  // Get responses from store (stable reference)
  const responsesForCurrentQuestion = useSurveyStore(
    state => state.answers[String(currentQuestionId)]
  )

  // Compute currentAnswer with useMemo to prevent infinite re-renders
  // For multiSelect, returns array of surveyQuestionResponseIds
  const currentAnswer = useMemo(() => {
    if (
      !responsesForCurrentQuestion ||
      responsesForCurrentQuestion.length === 0
    )
      return undefined

    // If it's a multiSelect question, return array of all surveyQuestionResponseIds
    if (currentQuestion?.type === 'multiSelect') {
      const responseIds = responsesForCurrentQuestion
        .map(r => r.surveyQuestionResponseId)
        .filter(id => id !== undefined && id !== null)
        .sort((a, b) => a - b) // Sort for stable comparison
      return responseIds.length > 0 ? responseIds : []
    }

    // For single response questions, return the most recent response value
    const mostRecent =
      responsesForCurrentQuestion[responsesForCurrentQuestion.length - 1]
    return mostRecent?.responseValue ?? mostRecent?.surveyQuestionResponseId
  }, [responsesForCurrentQuestion, currentQuestion?.type])

  // Fetch responses from database only if not provided via SSR
  // After initialization, Zustand becomes the single source of truth
  const { data: responsesData } = useQuery(getClientResponses, {
    variables: {
      clientId: String(clientId || ''),
      surveyId: String(survey?.id || ''),
    },
    skip:
      !clientId || !survey?.id || isInitialized || existingResponses.length > 0, // Skip if we already have SSR data
    fetchPolicy: 'network-only', // Always fetch fresh data on initial load
  })

  // Initialize Zustand store from database responses once on mount
  useEffect(() => {
    if (
      isInitialized ||
      (!responsesData?.client?.cmsSurveyQuestionResponses &&
        existingResponses.length === 0)
    ) {
      return
    }

    // Combine SSR responses with fetched responses
    // For multiple responses per question, we want to keep all of them
    const responseMap = new Map<number, SurveyQuestionResponse[]>()

    // First add SSR responses
    existingResponses.forEach(response => {
      const questionId = response.surveyQuestionId
      if (!responseMap.has(questionId)) {
        responseMap.set(questionId, [])
      }
      responseMap.get(questionId)!.push(response)
    })

    // Then add/override with fetched responses (they take precedence)
    if (responsesData?.client?.cmsSurveyQuestionResponses) {
      responsesData.client.cmsSurveyQuestionResponses.forEach(
        (response: SurveyQuestionResponse) => {
          const questionId = response.surveyQuestionId
          if (!responseMap.has(questionId)) {
            responseMap.set(questionId, [])
          }
          // Check if this exact response already exists (by id)
          const existing = responseMap.get(questionId)!
          if (!existing.find(r => r.id === response.id)) {
            existing.push(response)
          }
        }
      )
    }

    // Flatten all responses into a single array
    const allResponses: SurveyQuestionResponse[] = []
    responseMap.forEach(responses => {
      allResponses.push(...responses)
    })

    // Initialize Zustand store with all responses
    // No need for questions or transformation function - just pass responses directly
    initializeFromResponses(allResponses)

    // Clear errors for questions with skipValue set (e.g., insuranceMembershipId with skipMembershipId)
    const state = useSurveyStore.getState()
    Object.entries(state.answers).forEach(([questionId, responses]) => {
      const mostRecent = responses[responses.length - 1]
      if (mostRecent?.metadata?.skipMembershipId === true) {
        const question = allQuestions.find(q => String(q.id) === questionId)
        if (question?.type === 'insuranceMembershipId') {
          state.setError(questionId, null)
        }
      }
    })
  }, [
    responsesData?.client?.cmsSurveyQuestionResponses,
    existingResponses,
    allQuestions,
    isInitialized,
    initializeFromResponses,
  ])

  const currentSkipValue = useSurveyStore(state => {
    const responses = state.answers[String(currentQuestionId)]
    if (!responses || responses.length === 0) return undefined
    const mostRecent = responses[responses.length - 1]
    return mostRecent?.metadata?.skipMembershipId ?? false
  })

  // Only show error if field has been touched
  const currentError = useSurveyStore(state => {
    const touched = state.touched[String(currentQuestionId)] || false
    const error = touched ? state.errors[String(currentQuestionId)] : undefined

    // Clear error if skipValue is true for insuranceMembershipId questions
    const responses = state.answers[String(currentQuestionId)]
    if (
      currentQuestion?.type === 'insuranceMembershipId' &&
      responses &&
      responses.length > 0
    ) {
      const mostRecent = responses[responses.length - 1]
      if (mostRecent?.metadata?.skipMembershipId === true) {
        return undefined
      }
    }

    return error
  })

  // Calculate current question index (using filtered questions)
  const currentQuestionIndex = useMemo(() => {
    const index = filteredQuestions.findIndex(
      q => String(q.id) === String(currentQuestionId)
    )
    return index === -1 ? 0 : index
  }, [filteredQuestions, currentQuestionId])

  // Calculate progress bar overrides for sign-up survey
  // Progress resets after InsuranceCoveredLoading question
  const progressOverrides = useMemo(() => {
    // Find the InsuranceCoveredLoading question index
    const insuranceLoadingIndex = filteredQuestions.findIndex(
      q => q.component === 'InsuranceCoveredLoading'
    )

    // If not found or not a sign-up survey with this component, use default behavior
    if (insuranceLoadingIndex === -1) {
      return {
        overrideCurrentIndex: undefined,
        overrideTotalQuestions: undefined,
      }
    }

    // If current question is at or before InsuranceCoveredLoading
    if (currentQuestionIndex <= insuranceLoadingIndex) {
      return {
        overrideCurrentIndex: currentQuestionIndex,
        overrideTotalQuestions: insuranceLoadingIndex + 1, // +1 because it's 0-indexed
      }
    }

    // After InsuranceCoveredLoading, start progress over
    const questionsAfterLoading =
      filteredQuestions.length - insuranceLoadingIndex - 1
    const indexAfterLoading = currentQuestionIndex - insuranceLoadingIndex - 1

    return {
      overrideCurrentIndex: indexAfterLoading,
      overrideTotalQuestions: questionsAfterLoading,
    }
  }, [filteredQuestions, currentQuestionIndex])

  // Track Amplitude page view for each question
  useEffect(() => {
    if (!currentQuestion) return

    AnalyticsManager.logEvent('Survey Question - Page View', {
      'Question ID': currentQuestion.id,
      'Question Text': currentQuestion.text,
      'Question Type': currentQuestion.type,
      'Question Index': currentQuestionIndex + 1,
      'Total Questions': filteredQuestions.length,
      'Survey ID': survey.id,
      'Survey Name': survey.name,
      'Client ID': clientId,
      'Is Required': currentQuestion.isRequired || false,
    })
  }, [
    currentQuestion,
    currentQuestionId,
    currentQuestionIndex,
    filteredQuestions.length,
    survey.id,
    survey.name,
    clientId,
  ])

  const isFirstQuestion = currentQuestionIndex === 0
  const isLastQuestion = currentQuestionIndex === filteredQuestions.length - 1

  // Use mutation without cache update (Zustand is the source of truth)
  const [createSurveyQuestionResponsesMutationFn] = useMutation(
    createSurveyQuestionResponsesMutation
  )

  // Build responseByQuestionId from Zustand store for components that need it
  // This is now much simpler - just convert the store structure to the expected format
  const responseByQuestionId = useMemo(() => {
    const map: Record<number, SurveyQuestionResponse[]> = {}

    // Zustand store already has the correct structure - just convert keys to numbers
    Object.entries(allAnswers).forEach(([questionIdStr, responses]) => {
      const questionId = Number(questionIdStr)
      if (!isNaN(questionId)) {
        map[questionId] = responses
      }
    })

    return map
  }, [allAnswers])

  // Wrapper function to match the expected interface
  const createSurveyQuestionResponses = useCallback(
    async ({
      clientId: paramClientId,
      coachId: paramCoachId,
      surveyQuestionId,
      surveyQuestionResponseIds,
      responseValue,
      modelPath,
      metadata,
    }: {
      clientId?: number
      coachId?: number
      surveyQuestionId: number
      surveyQuestionResponseIds?: number[]
      responseValue?: string | null
      modelPath?: string
      metadata?: Record<string, any>
    }) => {
      return await createSurveyQuestionResponsesMutationFn({
        variables: {
          surveyId: String(survey.id),
          clientId: paramClientId ? String(paramClientId) : undefined,
          coachId: paramCoachId ? String(paramCoachId) : undefined,
          surveyQuestionId: String(surveyQuestionId),
          surveyQuestionResponseIds: surveyQuestionResponseIds?.map(String),
          responseValue,
          modelPath,
          metadata,
        },
      })
    },
    [createSurveyQuestionResponsesMutationFn, survey?.id]
  )

  // Note: No longer loading from cache on question change
  // Zustand is now the single source of truth, initialized once on mount

  // Handle answer change
  const handleAnswerChange = useCallback(
    (value: any, metadata?: Record<string, any>) => {
      const questionId = String(currentQuestionId)

      setAnswer(questionId, value)

      // Mark that user interacted with this question (for auto-continue)
      lastInteractedQuestionIdRef.current = questionId

      // If metadata is provided, store it (e.g., for payer search)
      if (metadata) {
        setMetadata(questionId, metadata)
      }

      // Only validate if field has been touched
      const touched = useSurveyStore.getState().touched[questionId] || false
      if (touched && currentQuestion) {
        const skipValue = getSkipValue(questionId)
        const error = validateAnswer(currentQuestion, value, skipValue)
        setStoreError(questionId, error)
      } else {
        // Clear error if field hasn't been touched yet
        setStoreError(questionId, null)
      }
    },
    [
      currentQuestionId,
      currentQuestion,
      setAnswer,
      setMetadata,
      setStoreError,
      getSkipValue,
    ]
  )

  // Handle blur - mark as touched and validate
  const handleBlur = useCallback(() => {
    const questionId = String(currentQuestionId)
    setTouched(questionId, true)

    // Validate after marking as touched
    if (currentQuestion) {
      const value = getAnswer(questionId)
      const skipValue = getSkipValue(questionId)
      const error = validateAnswer(currentQuestion, value, skipValue)
      setStoreError(questionId, error)
    }
  }, [
    currentQuestionId,
    currentQuestion,
    setTouched,
    setStoreError,
    getAnswer,
    getSkipValue,
  ])

  // Handle skip change (for insuranceMembershipId)
  const handleSkipChange = useCallback(
    (skipValue: boolean) => {
      const questionId = String(currentQuestionId)
      setSkipValue(questionId, skipValue)

      // Mark as touched when skip is changed
      setTouched(questionId, true)

      // Get current answer value for validation
      const answerValue = getAnswer(questionId)

      // Validate
      if (currentQuestion) {
        const error = validateAnswer(currentQuestion, answerValue, skipValue)
        setStoreError(questionId, error)
      }
    },
    [
      currentQuestionId,
      currentQuestion,
      setSkipValue,
      setTouched,
      setStoreError,
      getAnswer,
    ]
  )

  // Navigation handlers
  const handleBack = useCallback(() => {
    if (currentQuestionIndex > 0) {
      const previousQuestion = filteredQuestions[currentQuestionIndex - 1]
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, questionId: previousQuestion.id },
        },
        undefined,
        { shallow: true }
      )
    }
  }, [currentQuestionIndex, filteredQuestions, router])

  const handleContinue = useCallback(async () => {
    if (!currentQuestion) return

    const questionId = String(currentQuestionId)
    const value = getAnswer(questionId)
    const skipValue = getSkipValue(questionId)
    const responses = useSurveyStore.getState().answers[questionId]
    const metadata = responses?.[responses.length - 1]?.metadata

    // Validate
    const error = validateAnswer(currentQuestion, value, skipValue)
    if (error) {
      setStoreError(questionId, error)
      return
    }

    // Prepare submission payload
    const payload = prepareSubmissionPayload(
      currentQuestion,
      value,
      skipValue,
      metadata
    )

    // Navigate immediately (optimistic navigation)
    // Use filteredQuestions for navigation to respect conditional skipping
    if (currentQuestionIndex < filteredQuestions.length - 1) {
      const nextQuestion = filteredQuestions[currentQuestionIndex + 1]
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, questionId: nextQuestion.id },
        },
        undefined,
        { shallow: true }
      )
    }

    // Submit answer in background (skip if no payload or freeFormSkip)
    if (payload) {
      setSubmitting(true)
      setStoreError(questionId, null)

      try {
        const mutationResult = await createSurveyQuestionResponses({
          clientId,
          coachId,
          surveyQuestionId: payload.surveyQuestionId,
          surveyQuestionResponseIds:
            'surveyQuestionResponseIds' in payload
              ? payload.surveyQuestionResponseIds
              : undefined,
          responseValue:
            'responseValue' in payload ? payload.responseValue || null : null,
          modelPath: payload.modelPath,
          metadata:
            'metadata' in payload
              ? {
                  ...payload.metadata,
                  //   location: 'web',
                }
              : undefined,
          //   : { location: 'web' },
        })
        setSubmitting(false)
      } catch (err: any) {
        // Handle error: show error message but don't revert navigation
        // User can navigate back if needed
        setStoreError(
          questionId,
          err?.message ||
            'Failed to save your answer. Please try again or go back to retry.'
        )
        console.error('Error submitting answer:', err)
      } finally {
        setSubmitting(false)
      }
    }
  }, [
    currentQuestion,
    currentQuestionId,
    currentQuestionIndex,
    filteredQuestions,
    router,
    getAnswer,
    getSkipValue,
    createSurveyQuestionResponses,
    clientId,
    coachId,
    setStoreError,
    setSubmitting,
  ])

  // Track the last questionId that the user interacted with (via handleAnswerChange)
  // This allows us to distinguish between user selections and loading existing answers
  const lastInteractedQuestionIdRef = useRef<string | null>(null)

  // Ref for the question container to focus inputs
  const questionContainerRef = useRef<HTMLDivElement>(null)

  const handleFinalSubmit = useCallback(async () => {
    if (!currentQuestion) return

    const questionId = String(currentQuestionId)
    const value = getAnswer(questionId)
    const skipValue = getSkipValue(questionId)
    const responses = useSurveyStore.getState().answers[questionId]
    const metadata = responses?.[responses.length - 1]?.metadata

    // Mark as touched before validating
    setTouched(questionId, true)

    // Validate
    const error = validateAnswer(currentQuestion, value, skipValue)
    if (error) {
      setStoreError(questionId, error)
      return
    }

    // Prepare submission payload
    const payload = prepareSubmissionPayload(
      currentQuestion,
      value,
      skipValue,
      metadata
    )

    // Skip if no payload (freeFormSkip or empty value)
    if (!payload) {
      setSubmitting(false)
      // Still redirect to coach-matches if it's the last question
      router.push('/coach-matches?from=sign-up')
      return
    }

    setSubmitting(true)
    setStoreError(questionId, null)

    try {
      const finalMutationResult = await createSurveyQuestionResponses({
        clientId,
        coachId,
        surveyQuestionId: payload.surveyQuestionId,
        surveyQuestionResponseIds:
          'surveyQuestionResponseIds' in payload
            ? payload.surveyQuestionResponseIds
            : undefined,
        responseValue:
          'responseValue' in payload
            ? payload.responseValue ?? undefined
            : undefined,
        modelPath: payload.modelPath,
        metadata:
          'metadata' in payload
            ? {
                ...payload.metadata,
                location: 'web',
              }
            : { location: 'web' },
      })

      setSubmitting(false)
      // On success: redirect to coach-matches with explicit flow indicator
      router.push('/coach-matches?from=sign-up')
    } catch (err: any) {
      // On error: show error message, keep user on page
      setStoreError(
        questionId,
        err?.message || 'Failed to submit your answer. Please try again.'
      )
      console.error('Error submitting final answer:', err)
      setSubmitting(false)
    }
  }, [
    currentQuestion,
    currentQuestionId,
    router,
    getAnswer,
    getSkipValue,
    createSurveyQuestionResponses,
    clientId,
    coachId,
    setStoreError,
    setSubmitting,
    setTouched,
  ])

  // Auto-continue for single-response questions (select, dropdown, boolean)
  // but NOT for date, text, longText, or multiSelect
  // Triggers when user selects an answer (even if same as before), not when loading existing answers
  // For last question, automatically submits instead of continuing
  useEffect(() => {
    if (!currentQuestion) return

    const questionId = String(currentQuestionId)
    const value = getAnswer(questionId)

    const isSingleResponseQuestion =
      currentQuestion.type === 'select' ||
      currentQuestion.type === 'singleSelect' ||
      currentQuestion.type === 'dropdown' ||
      currentQuestion.type === 'boolean'

    const isTextInputQuestion =
      currentQuestion.type === 'text' ||
      currentQuestion.type === 'longText' ||
      currentQuestion.type === 'date'

    // Only auto-continue/submit if:
    // 1. It's a single-response question (not text input)
    // 2. Answer is valid
    // 3. User just interacted with this question (handleAnswerChange was called)
    // 4. Not currently submitting
    const wasJustInteracted = lastInteractedQuestionIdRef.current === questionId

    if (
      isSingleResponseQuestion &&
      !isTextInputQuestion &&
      value !== undefined &&
      value !== null &&
      value !== '' &&
      wasJustInteracted &&
      !isSubmitting
    ) {
      // Clear the interaction flag to prevent re-triggering
      lastInteractedQuestionIdRef.current = null

      // Mark as touched and validate
      setTouched(questionId, true)
      const skipValue = getSkipValue(questionId)
      const error = validateAnswer(currentQuestion, value, skipValue)
      setStoreError(questionId, error)

      // Only auto-continue/submit if there's no validation error
      if (!error) {
        // Small delay to allow UI to update
        const timeoutId = setTimeout(() => {
          if (isLastQuestion) {
            handleFinalSubmit()
          } else {
            handleContinue()
          }
        }, 300)

        return () => clearTimeout(timeoutId)
      }
    }
  }, [
    currentQuestion,
    currentQuestionId,
    currentAnswer,
    isLastQuestion,
    isSubmitting,
    getAnswer,
    getSkipValue,
    setTouched,
    setStoreError,
    handleContinue,
    handleFinalSubmit,
  ])

  // Check if continue button should be disabled
  const isContinueDisabled = useMemo(() => {
    if (isSubmitting) return true
    if (!currentQuestion) return true // Disable if no question is loaded

    // For freeFormSkip, always allow continue (it's informational)
    if (currentQuestion.type === 'freeFormSkip') {
      return false
    }

    // For insuranceMembershipId, if skip is checked, allow continue
    if (currentQuestion.type === 'insuranceMembershipId' && currentSkipValue) {
      return false
    }

    // For required questions, check if answer exists and is valid
    if (currentQuestion.isRequired) {
      // Use validation to check if answer is valid
      const error = validateAnswer(
        currentQuestion,
        currentAnswer,
        currentSkipValue
      )

      return !!error // Disable if there's a validation error
    }

    return false
  }, [currentQuestion, currentAnswer, currentSkipValue, isSubmitting])

  // Handle Enter key to proceed through survey
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Only handle Enter key
      if (event.key !== 'Enter') return

      // Don't trigger if user is typing in an input/textarea
      const target = event.target as HTMLElement
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        // Allow Enter in text inputs for multi-line text
        if (target.tagName === 'TEXTAREA') {
          return
        }
        // For single-line inputs, prevent default and proceed
        // But only if the input is not a date picker or other special input
        if (
          target.tagName === 'INPUT' &&
          target.getAttribute('type') === 'date'
        ) {
          return
        }
      }

      // Don't trigger if submitting or continue is disabled
      if (isSubmitting || isContinueDisabled) {
        return
      }

      // Prevent default form submission behavior
      event.preventDefault()

      // Proceed to next question or submit
      if (isLastQuestion) {
        handleFinalSubmit()
      } else {
        handleContinue()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [
    isSubmitting,
    isContinueDisabled,
    isLastQuestion,
    handleContinue,
    handleFinalSubmit,
  ])

  // Auto-focus on the first focusable element when question changes
  useEffect(() => {
    if (!questionContainerRef.current || !currentQuestion) return

    // Small delay to ensure DOM is updated after question change
    const timeoutId = setTimeout(() => {
      const container = questionContainerRef.current
      if (!container) return

      // Find the first focusable element based on question type
      let focusableElement: HTMLElement | null = null

      // For text inputs, date inputs, textareas
      focusableElement =
        container.querySelector<HTMLInputElement>('input[type="text"]') ||
        container.querySelector<HTMLInputElement>('input[type="date"]') ||
        container.querySelector<HTMLInputElement>('input[type="email"]') ||
        container.querySelector<HTMLInputElement>('input[type="tel"]') ||
        container.querySelector<HTMLInputElement>('input[type="number"]') ||
        container.querySelector<HTMLTextAreaElement>('textarea') ||
        container.querySelector<HTMLInputElement>('input:not([type="hidden"])')

      // For select/dropdown questions, try to focus the select element
      if (!focusableElement) {
        focusableElement =
          container.querySelector<HTMLSelectElement>('select') ||
          container.querySelector<HTMLElement>('[role="combobox"]')
      }

      // For boolean/radio questions, focus the first radio button or checkbox
      if (!focusableElement) {
        focusableElement =
          container.querySelector<HTMLInputElement>('input[type="radio"]') ||
          container.querySelector<HTMLInputElement>('input[type="checkbox"]')
      }

      // For button-based selections (like single select with buttons), focus the first button
      if (!focusableElement) {
        focusableElement = container.querySelector<HTMLButtonElement>(
          'button:not([disabled]):not([type="submit"])'
        )
      }

      // Focus the element if found
      if (focusableElement) {
        try {
          focusableElement.focus()
          // For select elements, also try to open the dropdown
          if (
            focusableElement.tagName === 'SELECT' ||
            focusableElement.getAttribute('role') === 'combobox'
          ) {
            // Trigger a click to open dropdown (for react-select and similar)
            if (focusableElement.getAttribute('role') === 'combobox') {
              focusableElement.click()
            }
          }
        } catch (error) {
          // Silently fail if focus is not possible (e.g., element not visible)
          if (process.env.NODE_ENV === 'development') {
            console.warn('Could not focus element:', error)
          }
        }
      }
    }, 100) // Small delay to ensure DOM is ready

    return () => clearTimeout(timeoutId)
  }, [currentQuestionId, currentQuestion])

  if (!currentQuestion) {
    return (
      <div className={s.container}>
        <div className={s.error}>Question not found</div>
      </div>
    )
  }

  return (
    <div className={s.pageWrapper}>
      <div className={s.pageContent}>
        <div className={s.container}>
          <SimpleKickoffHeader
            onBackClick={!isFirstQuestion ? handleBack : undefined}
            backgroundColor="#EDF4FB"
            style={isMobile ? { paddingTop: '1rem' } : undefined}
          />
          <div className={s.progressIndicatorWrapper}>
            <ProgressIndicator
              currentQuestionIndex={currentQuestionIndex}
              totalQuestions={filteredQuestions.length}
              overrideCurrentIndex={progressOverrides.overrideCurrentIndex}
              overrideTotalQuestions={progressOverrides.overrideTotalQuestions}
            />
          </div>
          <div className={s.form}>
            <div ref={scrollContainerRef} className={s.scrollContainer}>
              <div
                ref={questionContainerRef}
                className={s.questionContainer}
                key={`question-${currentQuestion.id}`}>
                <QuestionFactory
                  question={currentQuestion}
                  value={currentAnswer}
                  skipValue={currentSkipValue}
                  error={currentError}
                  onChange={handleAnswerChange}
                  onBlur={handleBlur}
                  onSkipChange={handleSkipChange}
                  clientId={clientId}
                  responseByQuestionId={responseByQuestionId}
                  allQuestions={filteredQuestions}
                />
              </div>
            </div>
          </div>
        </div>
        {/* {currentError && <div className={s.error}>{currentError}</div>} */}
        {currentQuestion?.component !== 'InsuranceCoveredLoading' && (
          <NavigationControls
            onContinue={handleContinue}
            onSubmit={handleFinalSubmit}
            showContinue={!isLastQuestion}
            showSubmit={isLastQuestion}
            isSubmitting={isSubmitting}
            isContinueDisabled={isContinueDisabled}
            shouldApplyKeyboardPadding={
              currentQuestion.type === 'text' ||
              currentQuestion.type === 'longText' ||
              currentQuestion.type === 'date' ||
              currentQuestion.type === 'insuranceMembershipId' ||
              currentQuestion.type === 'weightPicker'
            }
          />
        )}
      </div>
    </div>
  )
}

export default QuestionPage
